import type {BranchPickerBranch} from '@github-ui/item-picker-branch/BranchPicker'
import {type ConnectionOperation, DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, type Environment, graphql} from 'react-relay'

import type {
  linkBranchesMutation,
  linkBranchesMutation$data,
  linkBranchesMutation$rawResponse,
} from './__generated__/linkBranchesMutation.graphql'

type LinkBranchesMutationParams = {
  environment: Environment
  input: {baseIssueId: string; linkingIds: string[]}
  onError: (error: Error) => void
  linkingBranches?: BranchPickerBranch[]
}

function buildOptimisticResponse(
  baseIssueId: string,
  linkingBranches: BranchPickerBranch[],
): linkBranchesMutation$rawResponse {
  return {
    linkBranches: {
      issue: {
        id: baseIssueId,
        linkedBranches: {
          nodes: linkingBranches.map(branch => ({
            id: `optimistic-linked-branch-${branch.id}`,
            ref: {
              __typename: 'Ref' as const,
              id: branch.id,
              name: branch.name,
              target: branch.target
                ? {
                    oid: branch.target.oid,
                    id: branch.target.id,
                    __typename: 'Commit',
                  }
                : null,
              repository: {
                id: branch.repository.id,
                nameWithOwner: branch.repository.nameWithOwner ?? '',
                defaultBranchRef: branch.repository.defaultBranchRef
                  ? {
                      name: branch.repository.defaultBranchRef.name,
                      id: branch.repository.defaultBranchRef.id,
                      target: branch.repository.defaultBranchRef.target
                        ? {
                            oid: branch.repository.defaultBranchRef.target.oid,
                            id: branch.repository.defaultBranchRef.target.id,
                            __typename: 'Commit',
                          }
                        : null,
                      associatedPullRequests: {
                        totalCount: branch.repository.defaultBranchRef.associatedPullRequests.totalCount,
                      },
                    }
                  : null,
              },
              associatedPullRequests: {
                totalCount: branch.associatedPullRequests.totalCount,
              },
            },
          })),
        },
      },
    },
  }
}

function toLinkedBranchesOperation(
  data: linkBranchesMutation$data | linkBranchesMutation$rawResponse,
  baseIssueId: string,
): ConnectionOperation | undefined {
  const issue = data.linkBranches?.issue
  if (!issue) return undefined
  return {type: 'setFields', id: baseIssueId, fields: {linkedBranches: issue.linkedBranches}}
}

export function linkBranchesMutation({
  environment,
  input: {baseIssueId, linkingIds},
  onError,
  linkingBranches,
}: LinkBranchesMutationParams) {
  const optimisticResponse = linkingBranches ? buildOptimisticResponse(baseIssueId, linkingBranches) : undefined

  const mutationId = createConnectionMutationId()
  const optimisticOperation = optimisticResponse
    ? toLinkedBranchesOperation(optimisticResponse, baseIssueId)
    : undefined

  const dispatchOperationError = (error: Error) => dispatchConnectionMutation({phase: 'error', mutationId, error})

  // Published before `commitMutation`: a same-tick terminal would find nothing pending to settle.
  if (optimisticOperation) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId,
      ownerId: baseIssueId,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation: optimisticOperation,
    })
  }

  let disposable
  try {
    disposable = commitMutation<linkBranchesMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation linkBranchesMutation($baseIssueId: ID!, $linkingIds: [ID!]!) @raw_response_type {
          linkBranches(input: {issueId: $baseIssueId, linkingIds: $linkingIds}) {
            issue {
              ... on Issue {
                linkedBranches(first: 25) {
                  nodes {
                    id
                    # Ref fields inlined rather than shared via a fragment
                    # because we're removing Relay and a graphql tagged query
                    # can't interpolate a shared selection string.
                    ref {
                      name
                      id
                      __typename
                      target {
                        oid
                        id
                        __typename
                      }
                      repository {
                        id
                        nameWithOwner
                        defaultBranchRef {
                          name
                          id
                          target {
                            oid
                            id
                            __typename
                          }
                          associatedPullRequests {
                            totalCount
                          }
                        }
                      }
                      associatedPullRequests {
                        totalCount
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: {baseIssueId, linkingIds},
      optimisticResponse,
      onError: error => {
        dispatchOperationError(error)
        onError(error)
      },
      onUnsubscribe: () => {
        dispatchOperationError(new Error('Linking branches was cancelled'))
      },
      onCompleted: (response, errors) => {
        const operation = toLinkedBranchesOperation(response, baseIssueId)
        if (!operation) {
          // The overlay rolls back, and `DevelopmentPicker` shows `couldNotUpdateLinkedBranches`
          // from here, so staying silent would revert the section with no explanation.
          const error = new Error(errors?.[0]?.message ?? 'Failed to link branches')
          dispatchOperationError(error)
          onError(error)
          return
        }
        dispatchConnectionMutation({
          phase: 'completed',
          mutationId,
          ownerId: baseIssueId,
          connectionPath: DOCUMENT_FIELDS_PATH,
          operation,
        })
      },
    })
  } catch (error) {
    dispatchOperationError(error instanceof Error ? error : new Error('Failed to link branches'))
    throw error
  }

  return disposable
}
